/**
 * useChecklistCategoryVisibility.js
 *
 * Hook for managing vessel-scoped checklist category visibility.
 * Controls which categories appear in the main Checklists tab.
 *
 * ANCHOR MODE compliant - Domain Spine v2
 */

import { useCallback, useMemo } from 'react';
import { useStorageState } from '../../useStorageState';
import { useVessel } from '../../../contexts/VesselContext';
import { usePermissions } from '../../usePermissions';
// import { useVesselCollections } from '../../useVesselCollections'; // Phase 2.6: Disabled for IndexedDB migration
import { logCRUDOperation, AUDIT_ACTIONS } from '../../../services/auditTrail';
import { CHECKLIST_TYPE_CONFIG } from '../../../config/operations/checklistImportConfig';

// Protected categories that cannot be hidden (safety-critical)
// Note: Pre-Departure is now under Navigation category, so only Emergency is protected
const PROTECTED_CATEGORIES = ['emergency'];

// Default visibility - all categories visible except those with hideFromChecklistTab
const getDefaultVisibility = () => {
  const visibility = {};
  Object.values(CHECKLIST_TYPE_CONFIG).forEach(config => {
    if (!config.isCustomType) {
      // Default to visible unless explicitly hidden in config
      visibility[config.id] = !config.hideFromChecklistTab;
    }
  });
  return visibility;
};

// RBAC helper - defaults to true if no RBAC system or user is in settings
// Note: usePermissions.can(action, resource) - action first, resource second
const canDo = (can, resource, action) => {
  try {
    if (!can || typeof can !== 'function') return true; // Default to allow if no RBAC
    // can(action, resource) is the correct signature
    const result = can(action, resource) || can('read', resource);
    return result !== false; // Default to true unless explicitly denied
  } catch {
    return true; // Default to allow
  }
};

/**
 * Hook for checklist category visibility
 */
export const useChecklistCategoryVisibility = () => {
  const { getCurrentVessel } = useVessel();
  const currentVessel = getCurrentVessel();
  const { can, currentUser } = usePermissions();
  // const { collections } = useVesselCollections(); // Phase 2.6: Disabled for IndexedDB migration

  // Vessel-scoped storage
  const { value: visibilityConfig, setValue: setVisibilityConfig } = useStorageState(
    'checklist_visibility',
    { version: 1, categories: getDefaultVisibility() }
  );

  // Ensure we have a valid config
  const config = useMemo(() => {
    if (!visibilityConfig || !visibilityConfig.categories) {
      return { version: 1, categories: getDefaultVisibility() };
    }
    return visibilityConfig;
  }, [visibilityConfig]);

  /**
   * Check if a category is visible
   */
  const isCategoryVisible = useCallback((categoryId) => {
    // Protected categories are always visible
    if (PROTECTED_CATEGORIES.includes(categoryId)) {
      return true;
    }
    // Check config, default to true if not set
    return config.categories[categoryId] !== false;
  }, [config]);

  /**
   * Check if a category is protected (cannot be hidden)
   */
  const isCategoryProtected = useCallback((categoryId) => {
    return PROTECTED_CATEGORIES.includes(categoryId);
  }, []);

  /**
   * Toggle category visibility
   */
  const toggleCategoryVisibility = useCallback(async (categoryId) => {
    // Cannot toggle protected categories
    if (PROTECTED_CATEGORIES.includes(categoryId)) {
      console.warn('[useChecklistCategoryVisibility] Cannot toggle protected category:', categoryId);
      return false;
    }

    // RBAC check
    if (!canDo(can, 'settings', 'update')) {
      console.warn('[useChecklistCategoryVisibility] Permission denied');
      return false;
    }

    const currentVisibility = config.categories[categoryId] !== false;
    const newVisibility = !currentVisibility;

    const updatedConfig = {
      ...config,
      categories: {
        ...config.categories,
        [categoryId]: newVisibility
      },
      lastModified: new Date().toISOString(),
      modifiedBy: { name: currentUser?.name, userId: currentUser?.id }
    };

    await setVisibilityConfig(updatedConfig);

    // Audit log
    // Phase 2.6: Audit temporarily disabled for IndexedDB migration
    // try {
    //   await logCRUDOperation(
    //     collections,
        AUDIT_ACTIONS.UPDATE,
        'checklist_category_visibility',
        {
          categoryId,
          vesselId: currentVessel?.id,
          newVisibility,
          previousVisibility: currentVisibility
        },
        { userId: currentUser?.id, name: currentUser?.name }
    //   );
    // } catch (e) {
    //   console.warn('[useChecklistCategoryVisibility] Audit log failed:', e);
    // }

    return true;
  }, [can, config, setVisibilityConfig, currentUser, currentVessel]);

  /**
   * Get all visible categories for the Checklists tab
   * User visibility toggle OVERRIDES config.hideFromChecklistTab
   * Only truly hidden: isCustomType or hideFromSettingsTab (like Familiarisation)
   */
  const getVisibleCategories = useCallback(() => {
    return Object.values(CHECKLIST_TYPE_CONFIG)
      .filter(cat => !cat.isCustomType && !cat.hideFromSettingsTab) // Exclude custom and familiarisation
      .filter(cat => isCategoryVisible(cat.id)); // User visibility toggle is the authority
  }, [isCategoryVisible]);

  /**
   * Get visibility summary for UI
   * Counts categories that CAN be toggled (not custom, not familiarisation)
   */
  const getVisibilitySummary = useCallback(() => {
    const all = Object.values(CHECKLIST_TYPE_CONFIG).filter(c => !c.isCustomType && !c.hideFromSettingsTab);
    const visible = all.filter(c => isCategoryVisible(c.id));
    return {
      total: all.length,
      visible: visible.length,
      hidden: all.length - visible.length
    };
  }, [isCategoryVisible]);

  return {
    // Queries
    isCategoryVisible,
    isCategoryProtected,
    getVisibleCategories,
    getVisibilitySummary,

    // Mutations
    toggleCategoryVisibility,

    // Metadata
    lastModified: config.lastModified,
    modifiedBy: config.modifiedBy
  };
};

export default useChecklistCategoryVisibility;
